import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { supportTicketsAPI } from '../../services/api';
import { buildWebClientContext } from '../../utils/webClientContext';
import { getRecentBrowserConsoleEvents, installBrowserConsoleCapture } from '../../utils/browserConsoleCapture';
import { useAuth } from '../../contexts/AuthContext';
import { tokens } from '../../theme';
import { AnalyticsHero, AnalyticsScreen, InsightCallout, MetricTile, analyticsTokens, editorialSerif, panelSx } from './analytics/shared';
import cardMountains from '../../assets/card-mountains.png';

const TICKET_TYPES = [
  { value: 'generalInquiry', label: 'General inquiry' },
  { value: 'bugReport', label: 'Bug report' },
  { value: 'billingIssue', label: 'Billing issue' },
  { value: 'featureRequest', label: 'Feature request' },
];

const TYPE_LABELS = Object.fromEntries(TICKET_TYPES.map((type) => [type.value, type.label]));
const SUPPORT_GUIDANCE = [
  {
    title: 'Describe the issue',
    body: 'Mention the screen, the selected date, and what you expected to happen versus what actually happened.',
  },
  {
    title: 'Add timing details',
    body: 'If the problem affects a past day, include the exact date so support can review the matching daily summary.',
  },
  {
    title: 'Attach evidence',
    body: 'Screenshots, food names, and device-sync timing make subscription or nutrition issues much faster to resolve.',
  },
];

function formatRelativeDate(value) {
  if (!value) return 'just now';
  try {
    return `${formatDistanceToNowStrict(new Date(value), { addSuffix: true })}`;
  } catch {
    return 'just now';
  }
}

function formatAbsoluteDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
}

function formatFileSize(size) {
  if (!size) return '0 B';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageAttachment(attachment) {
  return Boolean(attachment?.mimeType?.startsWith('image/'));
}

function buildSupportFormData({
  type,
  subject,
  description,
  crashFlag,
  reproSteps,
  attachments,
  includeConsoleCapture,
}) {
  const browserConsole = type === 'bugReport' && includeConsoleCapture
    ? getRecentBrowserConsoleEvents()
    : [];
  const clientContext = buildWebClientContext({
    currentScreen: 'dashboard/support',
    reproSteps,
    crashFlag,
  });
  const enrichedContext = {
    ...clientContext,
    pageUrl: typeof window !== 'undefined' ? window.location.href : null,
    viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : null,
    browserConsole,
  };

  const formData = new FormData();
  formData.append('type', type);
  formData.append('subject', subject);
  formData.append('description', description);
  formData.append('platform', 'web');
  formData.append('currentScreen', enrichedContext.currentScreen);
  formData.append('pageUrl', enrichedContext.pageUrl || '');
  formData.append('viewport', enrichedContext.viewport || '');
  if (reproSteps) formData.append('reproSteps', reproSteps);
  formData.append('crashFlag', crashFlag ? 'true' : 'false');
  formData.append('clientContext', JSON.stringify(enrichedContext));
  formData.append('appInfo', JSON.stringify({ version: enrichedContext.appVersion, buildNumber: enrichedContext.buildNumber }));
  formData.append('deviceInfo', JSON.stringify({
    platform: 'Web',
    userAgent: enrichedContext.deviceModel,
    language: enrichedContext.locale,
  }));
  attachments.forEach((file) => {
    formData.append('attachments', file);
  });
  return formData;
}

function buildTicketActivity(ticket) {
  const entries = [
    {
      id: `opened-${ticket.id}`,
      label: 'Ticket opened',
      actor: ticket.userName || 'You',
      createdAt: ticket.createdAt,
      body: ticket.description,
    },
  ];

  const supportMessageAlreadyTracked = (ticket.messages || []).some(
    (message) => message?.authorType === 'support' && message?.text === ticket.adminResponse
  );

  if (ticket.adminResponse && !supportMessageAlreadyTracked) {
    entries.push({
      id: `response-${ticket.id}`,
      label: 'Support replied',
      actor: 'FastTime Support',
      createdAt: ticket.adminResponseAt || ticket.updatedAt,
      body: ticket.adminResponse,
    });
  }

  (ticket.messages || []).forEach((message, index) => {
    entries.push({
      id: `message-${ticket.id}-${index}`,
      label: message.authorType === 'support' ? 'Support update' : 'Follow-up',
      actor: message.author || (message.authorType === 'support' ? 'FastTime Support' : 'You'),
      createdAt: message.createdAt,
      body: message.text,
    });
  });

  return entries.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
}

function TicketMetadataRow({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: tokens.textSoft }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

export default function SupportPanel() {
  const { previewMode } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    type: 'generalInquiry',
    subject: '',
    description: '',
    reproSteps: '',
    crashFlag: false,
    includeConsoleCapture: true,
    attachments: [],
  });

  const isBugReport = form.type === 'bugReport';

  const loadTickets = useCallback(async () => {
    // Rendered unconditionally (SupportAnalytics has no props), reachable at
    // /dashboard/support via the nav rail. A preview visitor has no tokens,
    // so this would 401 and the auth interceptor would bounce the session to
    // /login instead of just showing an empty ticket list. previewMode comes
    // from AuthContext (captured once at session establishment), not
    // re-derived from window.location.search, which in-app navigation
    // rewrites without the query string.
    if (previewMode) {
      setTickets([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await supportTicketsAPI.getMine();
      setTickets(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [previewMode]);

  useEffect(() => {
    installBrowserConsoleCapture();
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (!tickets.length) {
      setSelectedTicketId('');
      return;
    }
    if (!selectedTicketId || !tickets.some((ticket) => ticket.id === selectedTicketId)) {
      setSelectedTicketId(tickets[0].id);
    }
  }, [selectedTicketId, tickets]);

  const selectedTicket = useMemo(
    () => tickets.find((ticket) => ticket.id === selectedTicketId) || tickets[0] || null,
    [selectedTicketId, tickets]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      setError('Subject and description are required.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const formData = buildSupportFormData(form);
      await supportTicketsAPI.create(formData);
      setSuccess('Support ticket submitted. We\'ll get back to you by email.');
      setForm({
        type: 'generalInquiry',
        subject: '',
        description: '',
        reproSteps: '',
        crashFlag: false,
        includeConsoleCapture: true,
        attachments: [],
      });
      await loadTickets();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setForm((current) => ({
      ...current,
      attachments: [...current.attachments, ...files].slice(0, 5),
    }));
    event.target.value = '';
  };

  const removeAttachment = (fileToRemove) => {
    setForm((current) => ({
      ...current,
      attachments: current.attachments.filter((file) => !(file.name === fileToRemove.name && file.size === fileToRemove.size)),
    }));
  };

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Support"
        subtitle="Report issues, send context-rich bug reports, and track the full status of your tickets."
        backgroundImage={cardMountains}
        chips={[`${tickets.length} ticket${tickets.length === 1 ? '' : 's'}`]}
      />

      {(error || success) && (
        <Alert
          severity={error ? 'error' : 'success'}
          sx={{ mb: 2, bgcolor: error ? 'rgba(230,124,115,0.08)' : 'rgba(123,196,142,0.1)' }}
          onClose={() => { setError(''); setSuccess(''); }}
        >
          {error || success}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Box sx={panelSx({ p: 2.25, height: '100%' })}>
            <Typography variant="h5" sx={{ color: tokens.cream, mb: 1.1 }}>
              What to include
            </Typography>
            <Stack spacing={1}>
              {SUPPORT_GUIDANCE.map((item) => (
                <InsightCallout key={item.title} title={item.title} body={item.body} />
              ))}
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <Box sx={panelSx({ p: 2.25, height: '100%' })}>
            <Typography variant="h5" sx={{ color: tokens.cream, mb: 1.1 }}>
              Support windows
            </Typography>
            <Stack spacing={1.2}>
              <MetricTile label="Response target" value="< 24h" footnote="For account and billing requests" />
              <MetricTile label="Best first step" value="Ticket + details" footnote="Use the form below so files and dashboard context stay attached" accent={analyticsTokens.accentStrong} />
              <MetricTile label="High-priority topics" value="Sync / billing" footnote="Fastest triage path for premium blockers" accent={analyticsTokens.clay} />
            </Stack>
          </Box>
        </Grid>
      </Grid>

      <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2}>
        <Box component="form" onSubmit={handleSubmit} sx={{ ...panelSx({ p: 2.5 }), flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 2 }}>
            New ticket
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              select
              fullWidth
              label="Type"
              value={form.type}
              onChange={(e) => setForm((current) => ({ ...current, type: e.target.value }))}
            >
              {TICKET_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Subject"
              value={form.subject}
              onChange={(e) => setForm((current) => ({ ...current, subject: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Description"
              value={form.description}
              onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={isBugReport ? 'Steps to reproduce' : 'Extra details (optional)'}
              value={form.reproSteps}
              onChange={(e) => setForm((current) => ({ ...current, reproSteps: e.target.value }))}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              sx={{ p: 1.25, border: `1px solid ${analyticsTokens.border}`, backgroundColor: 'rgba(255,255,255,0.02)' }}
            >
              <Box>
                <Typography variant="body2" sx={{ color: tokens.cream }}>
                  Attach screenshots or files
                </Typography>
                <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                  Up to 5 files. Images, PDF, JSON, TXT, CSV, and ZIP are supported.
                </Typography>
              </Box>
              <Button component="label" variant="outlined" type="button">
                Choose files
                <input hidden multiple type="file" accept="image/*,.pdf,.json,.txt,.csv,.zip" onChange={handleAttachmentChange} />
              </Button>
            </Stack>
            {form.attachments.length > 0 && (
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {form.attachments.map((file) => (
                  <Chip
                    key={`${file.name}-${file.size}`}
                    label={`${file.name} · ${formatFileSize(file.size)}`}
                    onDelete={() => removeAttachment(file)}
                    sx={{ borderRadius: 1, maxWidth: '100%' }}
                  />
                ))}
              </Stack>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={form.crashFlag}
                  onChange={(e) => setForm((current) => ({ ...current, crashFlag: e.target.checked }))}
                />
              }
              label="This involved a crash or data loss"
            />
            {isBugReport && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.includeConsoleCapture}
                    onChange={(e) => setForm((current) => ({ ...current, includeConsoleCapture: e.target.checked }))}
                  />
                }
                label={`Include recent browser warnings and errors (${getRecentBrowserConsoleEvents().length} captured)`}
              />
            )}
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit ticket'}
            </Button>
          </Stack>
        </Box>

        <Box sx={{ ...panelSx({ p: 2.5 }), flex: 1.25, minWidth: 0 }}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h5" sx={{ color: tokens.cream, fontFamily: editorialSerif }}>
              My tickets
            </Typography>
            {selectedTicket && (
              <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                Updated {formatRelativeDate(selectedTicket.updatedAt || selectedTicket.createdAt)}
              </Typography>
            )}
          </Stack>

          {loading ? (
            <Typography variant="body2" sx={{ color: tokens.textMuted }}>Loading…</Typography>
          ) : tickets.length === 0 ? (
            <Typography variant="body2" sx={{ color: tokens.textMuted }}>No tickets yet.</Typography>
          ) : (
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
              <Stack spacing={1} sx={{ flex: 0.95, minWidth: 0 }}>
                {tickets.map((ticket) => {
                  const attachmentCount = ticket.attachments?.length || ticket.imageUrls?.length || 0;
                  const hasConsoleContext = Boolean(ticket.clientContext?.browserConsole?.length);
                  return (
                    <Box
                      key={ticket.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedTicketId(ticket.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedTicketId(ticket.id);
                        }
                      }}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        border: `1px solid ${selectedTicket?.id === ticket.id ? tokens.accentStrong : analyticsTokens.border}`,
                        backgroundColor: selectedTicket?.id === ticket.id ? 'rgba(169,191,122,0.08)' : 'rgba(255,255,255,0.02)',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: tokens.cream, fontWeight: 600 }} noWrap>
                          {ticket.subject}
                        </Typography>
                        <Chip label={ticket.status || 'open'} size="small" sx={{ borderRadius: 1, textTransform: 'capitalize' }} />
                      </Stack>
                      <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block' }}>
                        {TYPE_LABELS[ticket.type] || ticket.type} · {ticket.priority || 'medium'} · {formatRelativeDate(ticket.createdAt)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: tokens.textSoft, mt: 0.75 }}>
                        {ticket.description}
                      </Typography>
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                        {ticket.clientContext?.crashFlag && <Chip label="Crash reported" size="small" sx={{ borderRadius: 1 }} />}
                        {attachmentCount > 0 && <Chip label={`${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`} size="small" sx={{ borderRadius: 1 }} />}
                        {hasConsoleContext && <Chip label={`${ticket.clientContext.browserConsole.length} console event${ticket.clientContext.browserConsole.length === 1 ? '' : 's'}`} size="small" sx={{ borderRadius: 1 }} />}
                        {ticket.adminResponse && <Chip label="Support replied" size="small" sx={{ borderRadius: 1 }} />}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>

              <Box sx={{ flex: 1.2, minWidth: 0, border: `1px solid ${analyticsTokens.border}`, p: 1.75, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                {selectedTicket ? (
                  <Stack spacing={1.5}>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.75 }}>
                        <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif }}>
                          {selectedTicket.subject}
                        </Typography>
                        <Chip label={selectedTicket.status || 'open'} size="small" sx={{ borderRadius: 1, textTransform: 'capitalize' }} />
                      </Stack>
                      <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                        {TYPE_LABELS[selectedTicket.type] || selectedTicket.type} · Submitted {formatAbsoluteDate(selectedTicket.createdAt)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 1.25,
                      }}
                    >
                      <TicketMetadataRow label="Priority" value={selectedTicket.priority || 'medium'} />
                      <TicketMetadataRow label="Last updated" value={formatAbsoluteDate(selectedTicket.updatedAt || selectedTicket.createdAt)} />
                      <TicketMetadataRow label="Screen" value={selectedTicket.clientContext?.currentScreen} />
                      <TicketMetadataRow label="Viewport" value={selectedTicket.clientContext?.viewport} />
                      <TicketMetadataRow label="Page URL" value={selectedTicket.clientContext?.pageUrl} />
                      <TicketMetadataRow label="Crash / data loss" value={selectedTicket.clientContext?.crashFlag ? 'Yes' : 'No'} />
                    </Box>

                    {selectedTicket.clientContext?.reproSteps && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 0.5 }}>Steps to reproduce</Typography>
                        <Typography variant="body2" sx={{ color: tokens.textSoft, whiteSpace: 'pre-wrap' }}>
                          {selectedTicket.clientContext.reproSteps}
                        </Typography>
                      </Box>
                    )}

                    {selectedTicket.attachments?.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 0.75 }}>Attachments</Typography>
                        <Stack spacing={1}>
                          {selectedTicket.attachments.map((attachment, index) => (
                            <Box
                              key={`${attachment.url || attachment.storagePath || attachment.originalName}-${index}`}
                              sx={{ p: 1, border: `1px solid ${analyticsTokens.border}` }}
                            >
                              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ color: tokens.cream }} noWrap>
                                    {attachment.originalName || `Attachment ${index + 1}`}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                    {attachment.mimeType || 'file'} · {formatFileSize(attachment.size)}
                                  </Typography>
                                </Box>
                                {attachment.url && (
                                  <Link href={attachment.url} target="_blank" rel="noreferrer" underline="hover">
                                    Open file
                                  </Link>
                                )}
                              </Stack>
                              {attachment.url && isImageAttachment(attachment) && (
                                <Box
                                  component="img"
                                  src={attachment.url}
                                  alt={attachment.originalName || 'Support attachment'}
                                  sx={{ mt: 1, width: '100%', maxHeight: 220, objectFit: 'cover', border: `1px solid ${analyticsTokens.border}` }}
                                />
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {selectedTicket.clientContext?.browserConsole?.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 0.75 }}>Browser diagnostics</Typography>
                        <Stack spacing={1}>
                          {selectedTicket.clientContext.browserConsole.map((entry, index) => (
                            <Box
                              key={`${entry.timestamp || 'console'}-${index}`}
                              sx={{ p: 1, border: `1px solid ${analyticsTokens.border}`, backgroundColor: 'rgba(255,255,255,0.02)' }}
                            >
                              <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block' }}>
                                {(entry.level || 'log').toUpperCase()} · {entry.source || 'console'} · {formatAbsoluteDate(entry.timestamp)}
                              </Typography>
                              <Typography variant="body2" sx={{ color: tokens.textSoft, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {entry.message}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    <Divider sx={{ borderColor: analyticsTokens.border }} />

                    <Box>
                      <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 0.75 }}>Ticket history</Typography>
                      <Stack spacing={1}>
                        {buildTicketActivity(selectedTicket).map((entry) => (
                          <Box key={entry.id} sx={{ p: 1.1, border: `1px solid ${analyticsTokens.border}` }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                              <Typography variant="body2" sx={{ color: tokens.cream, fontWeight: 600 }}>
                                {entry.label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                {formatRelativeDate(entry.createdAt)}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 0.5 }}>
                              {entry.actor} · {formatAbsoluteDate(entry.createdAt)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: tokens.textSoft, whiteSpace: 'pre-wrap' }}>
                              {entry.body}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                    Select a ticket to inspect its full history.
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      </Stack>
    </AnalyticsScreen>
  );
}
